/*

PW Text Overflow Handling (single string center-ellipsis)
Truncates from center of string, displaying start and end.
This is a CSS-only version that does not trigger forced reflow in JS and allows
variable width fonts.

TODO:
- Improve fallback styling when on a different bg color than $backgroundColor
- Implement select-all via triple click to match OS convention

*/

import React from 'react'
import T from 'prop-types'
import Timing from '../hoc/Timing'
import ReactDOM from 'react-dom'
import TextOverflowOverlay from './TextOverflowOverlay'
import { measureTextWidth } from '../utils'

const GLOBAL_HIDE_EVENTS = ['mousewheel', 'mousedown']

const MIN_SPLIT_LENGTH = 10

class TextOverflowCenterLite extends React.Component {
  static displayName = 'TextOverflowCenter'

  static propTypes = {
    tagName: T.string,
    className: T.string,
    value: T.string, // Only accepts single string children

    setTimer: T.func.isRequired,
    clearTimer: T.func.isRequired,
    delay: T.number,

    onMouseEnter: T.func,
    onMouseLeave: T.func,
    overlayClassName: T.string,
    watchMove: T.bool
  }

  static defaultProps = {
    tagName: 'div',
    delay: 500,
    watchMove: false
  }

  state = {
    valueParts: ['', '']
  }

  UNSAFE_componentWillMount() {
    this._splitValue(this.props.value || '')
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.value !== this.props.value) {
      this._splitValue(nextProps.value || '')
    }
  }

  componentWillUnmount() {
    this._destroyOverlay()
  }

  _splitValue(value) {
    let part1, part2
    if (value.length < MIN_SPLIT_LENGTH) {
      part1 = value
      part2 = ''
    } else {
      // Roughly split in half
      let splitAt = Math.ceil(value.length / 2)
      // Ensure we don't split adjacent to whitespace or the rendered spans will collapse together
      while (/\s/.test(value.substr(splitAt - 1, 2))) {
        splitAt--
      }
      part1 = value.substr(0, splitAt)
      part2 = value.substr(splitAt)
    }
    this.setState({valueParts: [part1, part2]})
  }

  _onMouseEnter = e => {
    const {
      onMouseEnter,
      delay
    } = this.props
    if (onMouseEnter) {
      onMouseEnter(e)
    }
    if (delay) {
      this.props.setTimer(this._checkOverflow, delay, 'delay')
    } else {
      this._checkOverflow()
    }
  }

  _onMouseLeave = e => {
    const {
      onMouseLeave
    } = this.props
    if (onMouseLeave) {
      onMouseLeave(e)
    }
    this._destroyOverlay()
  }

  _checkOverflow = () => {
    const el = ReactDOM.findDOMNode(this)
    const textWidth = measureTextWidth(this.props.value || '', el)
    if (textWidth > el.clientWidth) {
      this._showOverlay()
    }
  }

  _showOverlay = () => {
    const {
      value,
      overlayClassName
    } = this.props
    this._destroyOverlay() //just in case
    const el = ReactDOM.findDOMNode(this)

    const mountNode = (this._overlayMountNode = document.createElement('div'))
    mountNode.className = 'text_overflow_overlay_mount'
    document.body.appendChild(mountNode)

    ReactDOM.render(
      <TextOverflowOverlay
        source={el}
        content={value}
        className={overlayClassName}
      />,
      mountNode
    )
    GLOBAL_HIDE_EVENTS.forEach(name =>
      window.addEventListener(name, this._destroyOverlay, true)
    )

    if (this.props.watchMove) {
      this._watchMove()
    }
  }

  _watchMove = () => {
    const box = ReactDOM.findDOMNode(this).getBoundingClientRect()
    const origBox = this._origBBox
    if (origBox) {
      if (
        box.left !== origBox.left ||
        box.top !== origBox.top ||
        box.right !== origBox.right ||
        box.bottom !== origBox.bottom
      ) {
        this._destroyOverlay()
        return
      }
    } else {
      this._origBBox = box
    }
    this.props.setTimer(this._watchMove, 50, 'watchMove')
  }


  _destroyOverlay = () => {
    const mountNode = this._overlayMountNode
    if (mountNode && mountNode.parentNode) {
      ReactDOM.unmountComponentAtNode(mountNode)
      mountNode.parentNode.removeChild(mountNode)
      delete this._overlayMountNode
    }
    GLOBAL_HIDE_EVENTS.forEach(name =>
      window.removeEventListener(name, this._destroyOverlay, true)
    )
    this.props.clearTimer('delay')
    this.props.clearTimer('watchMove')
    delete this._origBBox
  }

  render() {
    const {
      className,
      tagName
    } = this.props
    const {
      valueParts
    } = this.state
    const Comp = tagName

    return (
      <Comp
        className={`text_overflow_center_lite ${className || ''}`}
        onMouseEnter={this._onMouseEnter}
        onTouchStart={this._onMouseEnter}
        onMouseLeave={this._onMouseLeave}
        onTouchEnd={this._onMouseLeave}
      >
        <span className="inner_wrap">
          <span className="first_half">
            <span className="mask_wrap" data-content={valueParts[0]}>
              <span className="display">{valueParts[0]}</span>
            </span>
          </span>
          <span className="second_half">{valueParts[1]}</span>
        </span>
      </Comp>
    )
  }
}

export default Timing(TextOverflowCenterLite)
